function About() {
  return (
    <div className="animate-fade-in mx-auto max-w-4xl py-12">
      <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
        About <span className="logo-font font-bold">Xhalin</span>
      </h1>

      <div className="space-y-8">
        <section className="card">
          <p className="mb-4 leading-relaxed text-gray-700">
            <span className="logo-font font-bold">Xhalin</span> exists for one
            purpose: to help people find calm, clarity, and control through
            intentional breathing. Life gets heavy, stress piles up fast, and
            sometimes you just need something simple and steady to bring you
            back to yourself. That&apos;s what this site is designed for — a
            calm place to breathe, reset, and build a habit that actually
            supports your wellbeing.
          </p>
          <p className="leading-relaxed text-gray-700">
            This project is personal. It was built out of real struggle, real
            healing, and a long road back to feeling human again.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-4 text-2xl font-semibold">My Story</h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            A few years ago, everything fell apart for me. When Hurricane Irma
            hit Sint Maarten, I lost everything I owned in a single moment. I
            had to return to Canada with nothing, and that&apos;s when the real
            battle began. PTSD hit hard. Panic attacks became a constant part of
            my life — not for weeks, but for years. I couldn&apos;t work. I
            couldn&apos;t live normally. It felt like my world had collapsed.
          </p>
          <p className="mb-4 leading-relaxed text-gray-700">
            But even in the middle of all that, I wasn&apos;t alone. My fiancé
            stayed by my side through the absolute worst moments, and that
            support helped me piece myself back together. Slowly, day by day, I
            started rebuilding. Not the things I owned — but me.
          </p>
          <p className="leading-relaxed text-gray-700">
            Today, I&apos;m back in Sint Maarten — the same place where
            everything was taken from me — and this time, I&apos;m here
            stronger, calmer, and getting better every day.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-4 text-2xl font-semibold">Why Xhalin Exists</h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            PTSD taught me something important: even when life hits you harder
            than you ever imagined, you still get up. You still keep going. That
            mindset is the heart of{' '}
            <span className="logo-font font-bold">Xhalin</span>.
          </p>
          <p className="mb-4 leading-relaxed text-gray-700">
            Breathing was one of the first things that truly helped me regain
            control. Not complicated routines — just simple, steady breaths.
            Over time, that small practice became a lifeline.
          </p>
          <p className="leading-relaxed text-gray-700">
            <span className="logo-font font-bold">Xhalin</span> is built to make
            that lifeline accessible, beautiful, and effortless. A place where
            you can show up for one minute or twenty. A place that welcomes you
            exactly as you are. A place designed to help you slow down, find
            your rhythm, and reset your mind.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-4 text-2xl font-semibold">Core Features</h2>
          <div className="space-y-4">
            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Guided Breathing Sessions
              </h3>
              <p className="leading-relaxed text-gray-700">
                Smooth, visual and audio-guided breathing cycles that help you
                stay in rhythm without overthinking anything.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Customizable Ratios
              </h3>
              <p className="leading-relaxed text-gray-700">
                Choose your inhale, hold, exhale, and empty phases to match your
                comfort level or your personal routine.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Circle &amp; Line Modes
              </h3>
              <p className="leading-relaxed text-gray-700">
                Pick between the classic expanding circle or a calm, flowing
                line animation — whichever feels more natural for your practice.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Soothing Backgrounds
              </h3>
              <p className="leading-relaxed text-gray-700">
                Select from calming background images or use a simple color
                backdrop. You get to set the mood of your session.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Progress Tracking &amp; Stats
              </h3>
              <p className="leading-relaxed text-gray-700">
                Your past sessions are saved so you can see how consistent
                you&apos;ve been, view your best day, and get a simple visual
                chart of your progress over time.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Session Templates
              </h3>
              <p className="leading-relaxed text-gray-700">
                If you have a favorite ratio or setup, save it as a template so
                you can start your next session instantly.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Global Leaderboard
              </h3>
              <p className="leading-relaxed text-gray-700">
                If you want a little motivation, you can compare your total
                breathing time with others around the world.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">Reminders</h3>
              <p className="leading-relaxed text-gray-700">
                Set reminders so you don&apos;t forget to breathe intentionally,
                whether once a day or on your own schedule.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-semibold text-primary-700">
                Clean, Calm Design
              </h3>
              <p className="leading-relaxed text-gray-700">
                Every part of{' '}
                <span className="logo-font font-bold">Xhalin</span> is
                intentionally designed to feel soothing, minimal, and
                distraction-free.
              </p>
            </div>
          </div>
        </section>

        <section className="card bg-gradient-to-r from-primary-50 to-secondary-50">
          <h2 className="mb-4 text-2xl font-semibold">My Mission</h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            If you&apos;re here, maybe life has been heavy for you too. Maybe
            you&apos;re just looking for a moment of peace.
          </p>
          <p className="mb-4 leading-relaxed text-gray-700">
            Either way, I hope{' '}
            <span className="logo-font font-bold">Xhalin</span> gives you the
            same relief and strength it gave me. If this site helps even one
            person find calm on a hard day, then building it was worth every
            second.
          </p>
          <p className="leading-relaxed text-gray-700">
            Let&apos;s breathe through the hard moments — and rise, one breath
            at a time.
          </p>
        </section>
      </div>
    </div>
  );
}

export default About;
